import { useEffect, useState } from "react";
import { MessageSquare } from "lucide-react";
import { api } from "@/lib/api";

/**
 * R145 — Live SMS preview panel inside the guest-pass modal. Debounces to
 * `POST /api/me/guest-passes/preview` on every keystroke (400ms) so the
 * resident sees exactly what the guest will read on their phone.
 */
export function SmsPreviewPanel({ form }) {
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!form.guest_name.trim() || !form.plate.trim()) { setPreview(null); return; }
    const timer = setTimeout(async () => {
      try {
        const body = {
          guest_name: form.guest_name.trim(),
          plate: form.plate.trim().toUpperCase(),
          hours: Number(form.hours),
          guest_phone: form.guest_phone.trim() || undefined,
          custom_note: form.custom_note.trim() || undefined,
        };
        if (form.scheduled_start_at) {
          body.scheduled_start_at = new Date(form.scheduled_start_at).toISOString();
        }
        const { data } = await api.post("/me/guest-passes/preview", body);
        setPreview(data);
      } catch { setPreview(null); /* silent — user is still typing */ }
    }, 400);
    return () => clearTimeout(timer);
  }, [form.guest_name, form.plate, form.hours, form.guest_phone,
      form.custom_note, form.scheduled_start_at]);

  if (!preview) return null;
  return (
    <div className="md:col-span-2 rounded-lg border border-orange-500/20 bg-orange-500/[0.04] p-3"
         data-testid="sms-preview-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-orange-300/80">
          <MessageSquare className="h-3 w-3" /> SMS preview
        </div>
        <div className="text-[10px] text-zinc-500 tabular-nums">
          {preview.char_count} chars · {preview.sms_segment_count} segment{preview.sms_segment_count === 1 ? "" : "s"}
        </div>
      </div>
      <div className="rounded-md bg-white/[0.04] border border-white/10 p-3 text-[13px] leading-snug text-zinc-200 whitespace-pre-wrap"
           data-testid="sms-preview-body">
        {preview.body}
      </div>
      {!preview.would_send && (
        <div className="mt-2 text-[11px] text-amber-300/90">⚠ {preview.reason}</div>
      )}
    </div>
  );
}
